import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import { formValidationConstants } from "../helpers/formValidations";
import { ALL_PAYERS_OPTION } from "../helpers/ruleApplicability";
import { IRuleBucket, useRuleBucket } from "../hooks/useRuleBucket";
import { RuleApplicabilityField } from "./RuleApplicabilityField";
import {
  applyToAllLabel,
  RuleBucketPayersField,
  RuleBucketSwitch,
} from "./RuleBucketPayers";

const Title = styled.h3`
  margin-bottom: 16px;
`;

const ProviderHint = styled.small`
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
`;

/** Shown instead of the conversion offer while a bucket edit applies to every payer. */
export const bucketConversionHint = (bucket: IRuleBucket) =>
  `Switch off "${applyToAllLabel(bucket)}" to convert this payer's copy.`;

interface IProviderAndPayerEditSectionProps {
  /**
   * CLE-2842 step 2 — the applicability the rule was loaded with. A Payer-Provider rule can be
   * converted into a Fallback rule here and back, except while a bucket edit applies to every
   * payer (one payer's copy is converted, never the whole bucket).
   */
  savedApplicability?: RulesetApplicability;
}

export function ProviderAndPayerEditSection({
  savedApplicability,
}: IProviderAndPayerEditSectionProps = {}) {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<any>();
  const isDefault = useWatch({ control, name: "isDefault" });
  // CLE-2842 — set only when this rule is one of several payers' copies of the same rule.
  const ruleBucket = useRuleBucket();
  const bucket = isDefault ? null : ruleBucket;
  const editsWholeBucket = !!bucket?.applyToAll;
  const { getFormErrorMessage, handleOnBlurField } = useFormValidation();
  const { data, isLoading } = useGetAllProvidersQuery();
  const { data: allPayers, isLoading: allPayersLoading } =
    useGetAllPayersQuery();
  const [filteredProviders, setFilteredProviders] = useState<
    { name: string; value: string }[]
  >([]);
  const [filteredPayers, setFilteredPayers] = useState<
    { name: string; value: number | null }[]
  >([]);

  const providers = useMemo(() => {
    if (isLoading || !data) return [];

    return data?.data.map((provider) => {
      return { name: provider.name, value: provider.id };
    });
  }, [data]);

  // CLE-2842 step 2 — "All payers" (payerId null) leads the payer choices.
  const payers = useMemo<{ name: string; value: number | null }[]>(() => {
    if (allPayersLoading || !allPayers) return [ALL_PAYERS_OPTION];
    return [
      ALL_PAYERS_OPTION,
      ...allPayers.data.map((payer: any) => {
        return { name: payer.name, value: payer.id };
      }),
    ];
  }, [allPayers]);

  const searchProvider = (event: { query: string }) => {
    setTimeout(() => {
      let filteredProvidersInternal;

      if (!event.query.trim().length) {
        filteredProvidersInternal = [...providers];
      } else {
        filteredProvidersInternal = providers.filter((provider) => {
          return provider.name
            .toLowerCase()
            .startsWith(event.query.toLowerCase());
        });
      }

      setFilteredProviders(filteredProvidersInternal);
    }, 250);
  };

  const searchPayer = (event: { query: string }) => {
    setTimeout(() => {
      let filteredPayersInternal;

      if (!event.query.trim().length) {
        filteredPayersInternal = [...payers];
      } else {
        filteredPayersInternal = payers.filter((payer: any) => {
          return payer.name.toLowerCase().startsWith(event.query.toLowerCase());
        });
      }

      setFilteredPayers(filteredPayersInternal);
    }, 250);
  };
  return (
    <div className="block bg-white border-round p-4 mb-4">
      <Title>Provider and Payer Section</Title>
      {/* CLE-2524: a Typical rule stays Typical. CLE-2842 step 2: a Payer-Provider rule and a
          Fallback rule convert into each other — one payer's copy at a time. */}
      <RuleApplicabilityField
        savedApplicability={savedApplicability}
        conversionBlockedHint={
          editsWholeBucket && bucket ? bucketConversionHint(bucket) : undefined
        }
        scopedFields={
          <>
            {bucket && <RuleBucketSwitch bucket={bucket} />}
            <div className="formgrid grid">
              <div className="field col-6">
                <Label htmlFor="hospitalName">Provider</Label>
                <Controller
                  data-testid="provider"
                  name="provider"
                  control={control}
                  rules={{
                    required: isDefault
                      ? false
                      : formValidationConstants.PROVIDER.PROVIDER,
                  }}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <>
                      <AutoComplete
                        dropdown
                        field="name"
                        id="provider"
                        value={value}
                        className="w-full"
                        suggestions={filteredProviders}
                        completeMethod={searchProvider}
                        dropdownAriaLabel="Select Provider"
                        placeholder="Select Provider"
                        // A bucket is one provider's rule; another provider's copy is made with Copy.
                        disabled={editsWholeBucket}
                        onChange={onChange}
                        onBlur={() => {
                          handleOnBlurField({
                            onBlur,
                            field: "provider",
                            trigger,
                          });
                        }}
                      />
                      {editsWholeBucket && (
                        <ProviderHint>
                          Every payer&apos;s copy shares this provider. Use Copy
                          to apply the rule to another provider.
                        </ProviderHint>
                      )}
                      {getFormErrorMessage("provider", errors)}
                    </>
                  )}
                />
              </div>
              <div className="field col-6">
                {editsWholeBucket && bucket ? (
                  <RuleBucketPayersField bucket={bucket} />
                ) : (
                  <>
                    <Label htmlFor="payerName">Payer Name</Label>
                    <Controller
                      name="payerName"
                      control={control}
                      rules={{
                        required: isDefault
                          ? false
                          : formValidationConstants.PAYER_NAME.NAME,
                      }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <>
                          <AutoComplete
                            dropdown
                            field="name"
                            id="payerName"
                            value={value}
                            className="w-full"
                            suggestions={filteredPayers}
                            completeMethod={searchPayer}
                            dropdownAriaLabel="Select Payer"
                            placeholder="Select Payer"
                            onChange={onChange}
                            onBlur={() => {
                              handleOnBlurField({
                                onBlur,
                                field: "payerName",
                                trigger,
                              });
                            }}
                          />
                          {getFormErrorMessage("payerName", errors)}
                        </>
                      )}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        }
      />
    </div>
  );
}
